import React, { useMemo, useRef, useState } from 'react';
import { View, Text, Image, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import ReactDOM from 'react-dom';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';

interface ContentBlock {
  type:
    | 'heading'
    | 'paragraph'
    | 'blockquote'
    | 'codeblock'
    | 'list'
    | 'image'
    | 'bullet_list'
    | 'ordered_list'
    | 'image_gallery';
  attrs?: Record<string, any>;
  content?: ContentNode[];
  marks?: Mark[];
  text?: string;
}

interface ContentNode {
  type?: string;
  text?: string;
  marks?: Mark[];
  content?: ContentNode[];
  attrs?: Record<string, any>;
}

interface Mark {
  type: 'bold' | 'italic' | 'underline' | 'strikethrough' | 'code' | 'link';
  attrs?: Record<string, any>;
}

interface RichContentRendererProps {
  contentJson: any;
  t?: (key: string) => string;
  onTagPress?: (tag: string) => void;
}

/**
 * RichContentRenderer - 轻量级 JSON 内容渲染器
 * 支持将编辑器 JSON 格式渲染为 React Native 组件
 * 
 * 排版规则：
 * - H1: 32px, margin-top 24px, margin-bottom 16px
 * - H2: 26px, margin-top 20px, margin-bottom 12px
 * - H3-H6: 渐进缩小
 * - 段落: 16px, 行高 24px, margin-bottom 12px
 * - 后续元素会自动向上收缩，避免过大的空白
 */
const RichContentRenderer: React.FC<RichContentRendererProps> = ({ contentJson, t, onTagPress }) => {
  const blocks = useMemo(() => {
    try {
      if (typeof contentJson === 'string') {
        return JSON.parse(contentJson);
      }
      return contentJson;
    } catch {
      return { content: [] };
    }
  }, [contentJson]);

  const parseTextWithTags = (text: string) => {
    if (!text || !text.includes('#')) return text;
    
    // Split by hashtag pattern, capturing the hashtag
    // Matches # followed by alphanumeric or CJK characters
    const parts = text.split(/(#[\w\u4e00-\u9fa5]+)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('#') && part.length > 1) {
        const tag = part.substring(1); // remove #
        return (
          <Text 
            key={index} 
            style={{ color: '#0ea5a4', fontWeight: '500' }}
            onPress={() => onTagPress?.(tag)}
          >
            {part}
          </Text>
        );
      }
      return <Text key={index}>{part}</Text>;
    });
  };

  const renderMarks = (marks?: Mark[]) => {
    const markStyles: any = {};
    marks?.forEach((mark) => {
      switch (mark.type) {
        case 'bold':
          markStyles.fontWeight = 'bold';
          break;
        case 'italic':
          markStyles.fontStyle = 'italic';
          break;
        case 'underline':
          markStyles.textDecorationLine = 'underline';
          break;
        case 'strikethrough':
          markStyles.textDecorationLine = 'line-through';
          break;
        case 'code':
          markStyles.fontFamily = 'monospace';
          markStyles.backgroundColor = '#f5f5f5';
          markStyles.paddingHorizontal = 4;
          markStyles.borderRadius = 2;
          markStyles.fontSize = 13;
          break;
      }
    });
    return markStyles;
  };

  // 簡單的圖片畫廊元件：窄屏單張、可橫向滑動；寬屏全寬輪播，附左右切換與 1/n 指示
  const ImageGallery: React.FC<{ images: Array<{ src: string; alt?: string; title?: string }>; aspectRatio?: number }>
    = ({ images, aspectRatio = 16 / 9 }) => {
    const scrollRef = useRef<ScrollView | null>(null);
    const [wrapWidth, setWrapWidth] = useState(0);
    const [index, setIndex] = useState(0);
    const [lightboxVisible, setLightboxVisible] = useState(false);

    // Hide bottom nav when lightbox is open
    React.useEffect(() => {
      if (lightboxVisible && typeof document !== 'undefined') {
        const portal = document.getElementById('app-bottom-nav-portal');
        if (portal) portal.style.setProperty('display', 'none', 'important');
        
        const elements = document.getElementsByClassName('app-bottom-nav-root');
        for (let i = 0; i < elements.length; i++) {
          (elements[i] as HTMLElement).style.setProperty('display', 'none', 'important');
        }

        return () => {
          if (portal) portal.style.removeProperty('display');
          for (let i = 0; i < elements.length; i++) {
            (elements[i] as HTMLElement).style.removeProperty('display');
          }
        };
      }
    }, [lightboxVisible]);

    const isWide = wrapWidth >= 768; // 粗略判斷寬屏
    const itemWidth = wrapWidth || 0;
    const itemHeight = Math.max(220, Math.round(itemWidth / (aspectRatio || (16 / 9))));

    const clamp = (i: number) => Math.max(0, Math.min(images.length - 1, i));
    const computeIndexFromEvent = (e: any) => {
      const x = e?.nativeEvent?.contentOffset?.x || 0;
      const pageW = e?.nativeEvent?.layoutMeasurement?.width || itemWidth || 1; // 保障不能為 0
      const i = Math.round(x / pageW);
      return clamp(i);
    };
    const handleMomentumEnd = (e: any) => {
      setIndex(computeIndexFromEvent(e));
    };
    
    const handleScroll = (e: any) => {
      const newIndex = computeIndexFromEvent(e);
      if (newIndex !== index) {
        setIndex(newIndex);
      }
    };

    const goTo = (i: number) => {
      const clamped = Math.max(0, Math.min(images.length - 1, i));
      setIndex(clamped);
      if (scrollRef.current && itemWidth > 0) {
        (scrollRef.current as any).scrollTo({ x: clamped * itemWidth, y: 0, animated: true });
      }
    };

    if (!images?.length) return null;

    return (
      <>
        <View
          onLayout={(e) => setWrapWidth(Math.round(e.nativeEvent.layout.width))}
          style={[styles.galleryCarouselWrap, { height: itemWidth ? itemHeight : undefined }]}
        >
          <ScrollView
            ref={scrollRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onMomentumScrollEnd={handleMomentumEnd}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            nestedScrollEnabled={true}
            style={{ touchAction: 'pan-x pan-y' } as any}
          >
            {images.map((img, i) => (
              <View key={i} style={{ width: itemWidth || '100%' }}>
                <TouchableOpacity 
                  activeOpacity={0.9} 
                  onPress={() => { setIndex(i); setLightboxVisible(true); }}
                  style={{ width: '100%', height: '100%' }}
                >
                  <Image
                    source={{ uri: img.src }}
                    style={{ width: '100%', height: itemHeight }}
                    onError={() => console.warn(t ? t('travelPostDetail.richContent.imageLoadFailed') : '图片加载失败:', img.src)}
                  />
                </TouchableOpacity>
              </View>
            ))}
          </ScrollView>

          {/* 1/n 指示器（右上角） */}
          {images.length > 1 && (
            <View style={styles.galleryCounter}>
              <Text style={styles.galleryCounterText}>{`${index + 1}/${images.length}`}</Text>
            </View>
          )}

          {/* 底部置中圓點指示器 */}
          {images.length > 1 && (
            <View style={[styles.galleryDotsWrap, { pointerEvents: 'box-none' }]}>
              {images.map((_, i) => (
                <TouchableOpacity key={i} onPress={() => goTo(i)} activeOpacity={0.8} style={styles.galleryDotTouch}>
                  <View style={[styles.galleryDot, i === index && styles.galleryDotActive]} />
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* 寬屏顯示左右切換按鈕 */}
          {isWide && images.length > 1 && (
            <>
              <TouchableOpacity style={[styles.galleryNavBtn, { left: 8 }]} activeOpacity={0.8} onPress={() => goTo(index - 1)}>
                <MdChevronLeft size={30} color="#333" />
              </TouchableOpacity>
              <TouchableOpacity style={[styles.galleryNavBtn, { right: 8 }]} activeOpacity={0.8} onPress={() => goTo(index + 1)}>
                <MdChevronRight size={30} color="#333" />
              </TouchableOpacity>
            </>
          )}
        </View>

        {/* Lightbox Overlay - Rendered via Portal to ensure it's on top of everything (including bottom nav) */}
        {lightboxVisible && typeof document !== 'undefined' && ReactDOM.createPortal(
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 2147483647,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'none', // Prevent background scrolling
            }}
            onClick={() => setLightboxVisible(false)}
          >
            <img
              src={images[index].src}
              alt={images[index].alt || ''}
              style={{
                maxWidth: '100vw',
                maxHeight: '100vh',
                objectFit: 'contain',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Close Button */}
            <button
              onClick={() => setLightboxVisible(false)}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#fff',
                fontSize: 24,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
              }}
            >
              <MdClose size={24} />
            </button>
            
            {/* Counter */}
            {images.length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 30,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: '#fff',
                  padding: '6px 16px',
                  borderRadius: 20,
                  fontSize: 14,
                  fontWeight: '500',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                }}
              >
                {index + 1} / {images.length}
              </div>
            )}

            {/* Title / Caption */}
            {images[index].title && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 80,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: '500',
                  maxWidth: '90%',
                  textAlign: 'center',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                }}
              >
                {images[index].title}
              </div>
            )}
            
            {/* Navigation Buttons */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIndex(clamp(index - 1));
                  }}
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    fontSize: 28,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                  }}
                >
                  <MdChevronLeft size={30} />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIndex(clamp(index + 1));
                  }}
                  style={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    fontSize: 28,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                  }}
                >
                  <MdChevronRight size={30} />
                </button>
              </>
            )}
          </div>,
          document.body
        )}
      </>
    );
  };

  const renderInline = (node: ContentNode, index: number): React.ReactNode => {
    if (node.type === 'text' || typeof node.text === 'string') {
      const textContent = node.text || node.content?.[0]?.text || '';
      return (
        <Text key={index} style={renderMarks(node.marks)}>
          {parseTextWithTags(textContent)}
        </Text>
      );
    }

    if (node.type === 'hard_break') {
      return <Text key={index}>{'\n'}</Text>;
    }

    return null;
  };

  const renderBlock = (block: ContentBlock, index: number, totalBlocks: number) => {
    const isLastBlock = index === totalBlocks - 1;
    const nextBlock = index < totalBlocks - 1 ? blocks.content[index + 1] : null;
    
    // 判断下一个元素是否是标题，如果是则减少当前元素的 margin-bottom
    const nextIsHeading = nextBlock?.type?.includes('heading');
    // normalize block type to handle different editor naming conventions
    const rawType = String(block.type || '').trim();
    const normType = rawType.toLowerCase().replace(/[_-]/g, '');

    switch (normType) {
      case 'heading': {
        const level = block.attrs?.level || 1;
        const config = {
          1: { fontSize: 24, marginTop: 0, marginBottom: 16, lineHeight: 32 },
          2: { fontSize: 18, marginTop: 16, marginBottom: 12, lineHeight: 26 },
          3: { fontSize: 16, marginTop: 14, marginBottom: 10, lineHeight: 24 },
          4: { fontSize: 15, marginTop: 12, marginBottom: 8, lineHeight: 22 },
          5: { fontSize: 14, marginTop: 12, marginBottom: 8, lineHeight: 20 },
          6: { fontSize: 13, marginTop: 12, marginBottom: 8, lineHeight: 18 },
        };
        const cfg = config[level as keyof typeof config] || config[6];
        
        return (
          <Text
            key={index}
            style={[
              styles.heading,
              {
                fontSize: cfg.fontSize,
                marginTop: index === 0 ? 0 : cfg.marginTop,
                marginBottom: isLastBlock ? 0 : cfg.marginBottom,
                lineHeight: cfg.lineHeight,
                fontWeight: '600',
              },
            ]}
          >
            {block.content?.map((node, i) => renderInline(node, i))}
          </Text>
        );
      }

      case 'paragraph': {
        return (
          <Text
            key={index}
            style={[
              styles.paragraph,
              {
                marginBottom: isLastBlock ? 0 : (nextIsHeading ? 8 : 12),
              },
            ]}
          >
            {block.content?.map((node, i) => renderInline(node, i))}
          </Text>
        );
      }

      case 'blockquote': {
        return (
          <View key={index} style={[styles.blockquote, { marginBottom: isLastBlock ? 0 : 12 }]}>
            <Text style={styles.blockquoteText}>
              {block.content?.map((node, i) => renderInline(node, i))}
            </Text>
          </View>
        );
      }

      case 'codeblock': {
        const code = block.content
          ?.map((node) => node.text || '')
          .join('')
          .trim();
        const language = block.attrs?.language || 'plaintext';
        return (
          <View key={index} style={[styles.codeblock, { marginBottom: isLastBlock ? 0 : 12 }]}>
            <Text style={styles.codeblockMeta}>{language}</Text>
            <Text style={styles.codeblockText}>{code}</Text>
          </View>
        );
      }

      case 'bulletlist':
      case 'orderedlist':
      case 'list': {
        // this covers naming like 'bulletList', 'orderedList', 'bullet_list', etc.
        return (
          <View key={index} style={[styles.listContainer, { marginBottom: isLastBlock ? 0 : 12 }]}>
            {block.content?.map((item: any, i: number) => {
              // item may be a listItem node whose content is an array of paragraph/inline nodes
              let itemContentNodes: ContentNode[] | undefined;
              if (item?.type && String(item.type).toLowerCase().includes('listitem')) {
                // typical structure: list -> listItem -> paragraph -> text
                // gather inner text nodes
                itemContentNodes = item.content?.[0]?.content ?? item.content ?? [];
              } else if (Array.isArray(item?.content)) {
                itemContentNodes = item.content;
              } else {
                itemContentNodes = [];
              }

              const marker = normType === 'bulletlist' ? '•' : `${i + 1}.`;

              return (
                <View key={i} style={styles.listItem}>
                  <Text style={styles.listItemMarker}>{marker}</Text>
                  <Text style={styles.listItemText}>
                    {itemContentNodes?.map((node: ContentNode, j: number) => renderInline(node, j))}
                  </Text>
                </View>
              );
            })}
          </View>
        );
      }

      case 'image': {
        const src = block.attrs?.src;
        const alt = block.attrs?.alt || (t ? t('travelPostDetail.richContent.imageAlt') : '图片');
        if (!src) return null;
        return (
          <View key={index} style={[styles.imageContainer, { marginBottom: isLastBlock ? 0 : 12 }]}>
            <Image
              source={{ uri: src }}
              style={styles.image}
              onError={() => console.warn(t ? t('travelPostDetail.richContent.imageLoadFailed') : '图片加载失败:', src)}
            />
            {alt && <Text style={styles.imageCaption}>{alt}</Text>}
          </View>
        );
      }

      case 'imagegallery': {
        const imgsInput = block.attrs?.images;
        const images: Array<{ src: string; alt?: string; title?: string }> = Array.isArray(imgsInput)
          ? imgsInput
              .map((it: any) => {
                if (!it) return null;
                if (typeof it === 'string') return { src: it };
                const src = it.src || it.url || it.uri || it.image || it.path;
                const alt = it.alt || it.caption || it.title;
                const title = it.title || it.caption;
                if (!src) return null;
                return { src, alt, title };
              })
              .filter(Boolean) as any
          : [];

        if (!images.length) return null;

        return (
          <View key={index} style={{ marginBottom: isLastBlock ? 0 : 12 }}>
            <ImageGallery images={images} aspectRatio={block.attrs?.aspectRatio} />
          </View>
        );
      }

      default:
        return null;
    }
  };

  if (!blocks?.content || blocks.content.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyText}>{t ? t('travelPostDetail.richContent.empty') : '暂无内容'}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {blocks.content.map((block: ContentBlock, index: number) =>
        renderBlock(block, index, blocks.content.length)
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  heading: {
    color: '#1a1a1a',
    fontWeight: '600',
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
    color: '#333',
  },
  blockquote: {
    borderLeftWidth: 4,
    borderLeftColor: '#ff6b35',
    paddingLeft: 12,
    backgroundColor: '#fafafa',
    paddingVertical: 10,
    paddingRight: 12,
    borderRadius: 4,
  },
  blockquoteText: {
    fontSize: 15,
    lineHeight: 22,
    color: '#666',
    fontStyle: 'italic',
  },
  codeblock: {
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  codeblockMeta: {
    fontSize: 11,
    color: '#999',
    backgroundColor: '#eeeeee',
    paddingHorizontal: 12,
    paddingVertical: 6,
    fontWeight: '500',
  },
  codeblockText: {
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 18,
    color: '#333',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  listContainer: {
    marginLeft: 0,
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 8,
    alignItems: 'flex-start',
  },
  listItemMarker: {
    fontSize: 16,
    fontWeight: '500',
    color: '#666',
    marginRight: 8,
    marginTop: 2,
    minWidth: 20,
  },
  listItemText: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
    color: '#333',
  },
  imageContainer: {
    paddingVertical: 12,
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    aspectRatio: 16 / 9,
    resizeMode: 'cover',
  },
  imageCaption: {
    fontSize: 12,
    color: '#999',
    paddingHorizontal: 12,
    paddingVertical: 8,
    textAlign: 'center',
  },
  galleryCarouselWrap: {
    position: 'relative',
    width: '100%',
    backgroundColor: '#0000000a',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  galleryCounter: {
    position: 'absolute',
    right: 8,
    top: 8,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  galleryCounterText: {
    color: '#fff',
    fontSize: 12,
  },
  galleryNavBtn: {
    position: 'absolute',
    top: '50%',
    marginTop: -24,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255,255,255,0.9)',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  galleryNavText: {
    color: '#333',
    fontSize: 28,
    fontWeight: '700',
  },
  galleryDotsWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  galleryDotTouch: {
    paddingHorizontal: 4,
    paddingVertical: 6,
  },
  galleryDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.45)',
  },
  galleryDotActive: {
    backgroundColor: '#fff',
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  emptyText: {
    fontSize: 14,
    color: '#ccc',
    textAlign: 'center',
    paddingVertical: 32,
  },
});

export default RichContentRenderer;
